import { ASK_USER_TOOL_NAME } from '../../ask-user-details.types';
import { ROUTINE_TOOL_NAMES } from './routineToolNames';

/**
 * Seed instruction for the "Suggest something" button on the create-routine
 * chat: instead of interviewing from a blank slate, the agent looks at how
 * this clinician actually works and comes back with a few themed routine
 * ideas to pick from.
 *
 * This is not a standalone wire prompt the way `buildAddRoutineChatSeedPrompt`
 * is. Its output is passed as the `seedInstructions` argument to
 * `buildCreateRoutineWithHeidiWirePrompt`
 * (`src/features/workflows/lib/createRoutineWithHeidi.ts`), which inserts it
 * BEFORE the `User request:` tail — the clinician's bubble ("Suggest
 * routines for me") stays that short tail, so
 * `displayBodyFromCreateRoutineWirePrompt` recovers only it rather than this
 * whole block. So this text has to reconcile with the standing
 * `CREATE_ROUTINE_WITH_HEIDI_WIRE_PROMPT` prefix rather than repeat or
 * contradict it — see the closing paragraph below.
 *
 * `ROUTINE_SUGGESTION_EXPLORATION_TOOL_NAMES` and
 * `ROUTINE_SUGGESTION_FORBIDDEN_TOOL_NAMES` are exported so tests can assert
 * the prompt structurally (every named tool appears, in the right clause)
 * instead of pinning its prose verbatim.
 */

export const ROUTINE_SUGGESTION_EXPLORATION_TOOL_NAMES = [
  'list_sessions',
  'get_sessions_by_date',
  'get_template_and_preferences',
  'list_letters',
] as const;

export const ROUTINE_SUGGESTION_FORBIDDEN_TOOL_NAMES = [
  'get_transcript',
  'get_consult_note',
  'get_patient_summary',
] as const;

export const ROUTINE_SUGGESTION_SEED_PREFIX =
  'Before you ask anything, look at how this clinician actually works using ' +
  `only the shape-only tools: \`${ROUTINE_SUGGESTION_EXPLORATION_TOOL_NAMES.join('`, `')}\`. Use ` +
  '`list_sessions` and `get_sessions_by_date` to see how much they see and ' +
  'when, `get_template_and_preferences` for what kind of clinician this is, ' +
  'and `list_letters` for whether letters are a large part of their work. ' +
  'Keep this exploration brief — a handful of calls, not an exhaustive ' +
  'audit — so your first reply does not take a minute to arrive.';

const FORBIDDEN_TOOL_INSTRUCTION =
  `Never call \`${ROUTINE_SUGGESTION_FORBIDDEN_TOOL_NAMES.join('`, `')}\` for this. Theming a ` +
  "clinician's week needs shape, not clinical content, and reading it is " +
  'slower as well as riskier. Do not name a patient anywhere in your reply.';

const MISSING_TOOL_INSTRUCTION =
  'One of those exploration tools may be unavailable in this run, and you ' +
  `may see a reply such as "Tool '${ROUTINE_SUGGESTION_EXPLORATION_TOOL_NAMES[0]}' is not ` +
  'available in this run." If that happens, do not retry the tool and do ' +
  'not apologise for it. Instead, ask one short question about how they ' +
  'work — for example what kind of visits they see most, or whether ' +
  'letters take up much of their time — and propose ideas from the answer.';

const PICKER_INSTRUCTION =
  'Once you have a sense of their work, offer three or four themed routine ' +
  `ideas with exactly one \`${ASK_USER_TOOL_NAME}\` call: kind 'choice', a ` +
  'single selection, and allow_other on so they can describe something ' +
  'else instead. Give each option a routine name as its label and one line ' +
  'of what it does and when it runs as its description. In the question ' +
  'text, say in one sentence what you noticed that grounded these ideas — ' +
  'without reciting raw counts or listing individual sessions.';

const HANDOFF_INSTRUCTION =
  'Once they pick an option or describe their own with the free-text row, ' +
  'interview them only for the details still genuinely missing, then make ' +
  `exactly one create call — \`${ROUTINE_TOOL_NAMES.create.exposed}\` or ` +
  `\`${ROUTINE_TOOL_NAMES.createScheduled.exposed}\` — using the canonical ` +
  'routine tool names. Do not summarize the routine in prose first; the ' +
  'approval card the tool surfaces is the review step.';

const WRAPPER_RECONCILIATION =
  'The instruction above to ask only for missing details, one question at ' +
  'a time, and then propose a routine still applies, but only from here on: ' +
  'exploring their work and offering the option card come first, and stand ' +
  'in for that first question, rather than fighting it.';

/**
 * Builds the wire text for the "Suggest something" seeded turn. The result
 * is not itself a complete wire prompt — pass it as `seedInstructions` to
 * `buildCreateRoutineWithHeidiWirePrompt`, which inserts it before the
 * `User request:` tail, alongside the standing prefix and the opening-message
 * continuity note.
 */
export function buildRoutineSuggestionSeedPrompt(): string {
  return [
    ROUTINE_SUGGESTION_SEED_PREFIX,
    FORBIDDEN_TOOL_INSTRUCTION,
    MISSING_TOOL_INSTRUCTION,
    PICKER_INSTRUCTION,
    HANDOFF_INSTRUCTION,
    WRAPPER_RECONCILIATION,
  ].join('\n\n');
}
